// Per-project intake form cache. Loaded when a project opens and refreshed
// after any mutation. Mirrors the structure of store/automationRules.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ApiIntakeForm } from "@juuplan/shared";
import {
  listIntakeForms,
  createIntakeForm as apiCreateIntakeForm,
  updateIntakeForm as apiUpdateIntakeForm,
  rotateIntakeToken as apiRotateIntakeToken,
  deleteIntakeForm as apiDeleteIntakeForm,
  type IntakeFormCreate,
  type IntakeFormUpdate,
  type IntakeFormCreateResponse,
  type IntakeFormRotateResponse,
} from "../lib/api";

export interface IntakeFormsState {
  loading: boolean;
  items: ApiIntakeForm[];
  projectId: number | null;
  error: string | null;
}

const initial: IntakeFormsState = {
  loading: true,
  items: [],
  projectId: null,
  error: null,
};

const intakeFormsSignal: Signal<IntakeFormsState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadIntakeForms(projectId: number): Promise<void> {
  try {
    const items = await listIntakeForms(projectId);
    intakeFormsSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    intakeFormsSignal.value = {
      loading: false,
      items: [],
      projectId,
      error: message,
    };
  } finally {
    inFlight = null;
  }
}

/**
 * Hook: subscribe to the intake forms for the given project.
 * Triggers a load when the projectId changes, and guards against returning
 * a prior project's data while the new load is still in flight.
 */
export function useIntakeForms(projectId: number): IntakeFormsState {
  useEffect(() => {
    const s = intakeFormsSignal.value;
    if (!inFlight && (s.projectId !== projectId || s.error !== null)) {
      intakeFormsSignal.value = {
        loading: true,
        items: [],
        projectId,
        error: null,
      };
      inFlight = loadIntakeForms(projectId);
    }
  }, [projectId]);

  const state = intakeFormsSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

/** Force a fresh fetch — use after any schema-changing mutation. */
export function refreshIntakeForms(projectId: number): Promise<void> {
  intakeFormsSignal.value = { ...intakeFormsSignal.value, loading: true };
  inFlight = loadIntakeForms(projectId);
  return inFlight;
}

// ---------- CRUD helpers ----------

/**
 * Create a new intake form and refresh the cache.
 * Returns the full create response including the one-time public_token + share_path.
 */
export async function addIntakeForm(
  projectId: number,
  body: IntakeFormCreate
): Promise<IntakeFormCreateResponse> {
  const created = await apiCreateIntakeForm(projectId, body);
  await refreshIntakeForms(projectId);
  return created;
}

/** Update an intake form and refresh the cache. */
export async function patchIntakeForm(
  formId: number,
  projectId: number,
  body: IntakeFormUpdate
): Promise<ApiIntakeForm> {
  const updated = await apiUpdateIntakeForm(formId, body);
  await refreshIntakeForms(projectId);
  return updated;
}

/** Rotate the share token for a form. Returns the one-time token + share_path. */
export async function rotateIntakeForm(
  formId: number,
  projectId: number
): Promise<IntakeFormRotateResponse> {
  const result = await apiRotateIntakeToken(formId);
  await refreshIntakeForms(projectId);
  return result;
}

/** Delete an intake form and remove it from the cache. */
export async function removeIntakeForm(
  formId: number,
  projectId: number
): Promise<void> {
  await apiDeleteIntakeForm(formId);
  const cur = intakeFormsSignal.value;
  if (cur.projectId === projectId) {
    intakeFormsSignal.value = {
      ...cur,
      items: cur.items.filter((f) => f.id !== formId),
    };
  }
}
